import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { H3, Icon, Text } from 'components';
import { StatusPayment } from 'components/Status';
import { lineHeight, theme, toRem } from 'design';
import { Align } from 'design/types';
import { PaymentMethodAccountNumber } from 'forms/PaymentMethodForm/styles';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { IPayoutGroup, PayoutStatus } from 'types';
import { centsToDollarsDisplay, formatDateAndTime, getPayoutStatusDisplayProps } from 'utils';

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  padding: ${toRem(theme.space.lg)};
  gap: ${toRem(theme.space.sm)};
  width: 100%;
  background-color: ${theme.color.white};
  border: ${toRem(1)} solid ${theme.color.neutral[100]};
  border-radius: ${toRem(theme.borderRadius.small)};
  box-shadow: ${theme.shadow.card};

  &:hover {
    cursor: pointer;
  }
`;

const Row = styled.div<{ justify?: Align; gap?: number }>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: ${({ justify }) => justify || 'space-between'};
  align-items: center;
  align-content: center;
  width: 100%;

  ${({ gap }) => gap && `gap: ${toRem(gap)};`}
`;

const Header = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.sm)};

  h3 {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
  }

  svg {
    fill: ${theme.color.primary[500]};
  }
`;

const PayoutTotalNet = styled.h2`
  font-size: ${toRem(theme.font.size['2xl'])};
  font-weight: ${theme.font.weight.semiBold};
`;

const PayoutItemNet = styled.p<{ isCanceled?: boolean }>`
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.medium};
  text-decoration: ${({ isCanceled }) => (isCanceled ? 'line-through' : 'none')};
`;

const PayoutStatusWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.sm)};
  padding: ${toRem(theme.space.xs)} 0;
`;

const PayoutStatusItem = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
`;

const PaymentMethodWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.xs)};
  width: 100%;
`;

const StyledPaymentMethodAccountNumberWrapper = styled.div`
  svg {
    border: 1px solid ${theme.color.neutral[200]};
    border-radius: ${toRem(2)};
  }
`;

const StyledStatusPayment = styled(StatusPayment)`
  div {
    font-size: ${toRem(theme.font.size.xxs)};
    line-height: ${lineHeight(theme.font.size.xxs)};
    font-weight: ${theme.font.weight.medium};
    color: ${theme.color.neutral[500]};
  }
`;

interface IPayoutGroupCardProps {
  payoutGroup: IPayoutGroup;
  isLoading?: boolean;
}

const PayoutGroupCard: React.FC<IPayoutGroupCardProps> = ({ payoutGroup, isLoading = false }) => {
  const totalNet = useMemo(() => payoutGroup?.payouts?.filter((p) => p.status !== PayoutStatus.CANCELED).reduce((sum, p) => sum + (p?.net || 0), 0) || 0, [payoutGroup?.payouts]);

  const onCardClick = () => {
    const firstPayoutId = payoutGroup?.payouts?.[0]?.paymentId;
    if (firstPayoutId) {
      browserHistory.push(`${routeConstants.private.employeePayoutDetails}/${firstPayoutId}`);
    }
  };

  return (
    <Layout
      role="link"
      tabIndex={0}
      aria-label={`View payout details for ${payoutGroup?.site?.name || 'site'} on ${payoutGroup?.paymentDate ? formatDateAndTime(payoutGroup.paymentDate) : 'unknown date'}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCardClick();
        }
      }}
      onClick={isLoading ? undefined : onCardClick}
      data-cy="payout-card"
      style={isLoading ? { cursor: 'default', pointerEvents: 'none' } : {}}
    >
      <Header data-cy="payout-card-header">
        {isLoading ? (
          <>
            <Row>
              <Skeleton width={120} height={14} />
              <Skeleton width={30} height={20} />
            </Row>
            <Row>
              <Skeleton width={150} height={20} />
              <Skeleton width={80} height={24} />
            </Row>
          </>
        ) : (
          <>
            <Row>
              <Text fontWeight="medium" size="xsmall" style={{ color: theme.color.neutral[500] }} data-cy="payout-card-payment-date">
                {formatDateAndTime(payoutGroup?.paymentDate)}
              </Text>
              <Icon icon="Next" width={6} height={9} data-cy="payout-card-next-icon" />
            </Row>

            <Row gap={8}>
              <H3 allowWordWrap fontWeight="medium" data-cy="payout-card-site-name">
                {payoutGroup?.site?.name}
              </H3>
              <PayoutTotalNet data-cy="payout-card-total-amount">{centsToDollarsDisplay(totalNet)}</PayoutTotalNet>
            </Row>
          </>
        )}
      </Header>

      <PayoutStatusWrapper data-cy="payout-card-status-wrapper">
        {isLoading ? (
          <PayoutStatusItem>
            <Skeleton width={100} height={16} />
            <Skeleton width={60} height={16} />
          </PayoutStatusItem>
        ) : (
          payoutGroup?.payouts?.map((payout) => {
            const { statusLabel, subStatusLabel, subStatusAppearance } = getPayoutStatusDisplayProps(payout);
            return (
              <React.Fragment key={payout.id}>
                <PayoutStatusItem data-cy="payout-card-status-item">
                  <PaymentMethodWrapper>
                    <StyledStatusPayment status={statusLabel} data-cy="status-payment" subStatusLabel={subStatusLabel} subStatusAppearance={subStatusAppearance} />
                    {payout?.paymentDestination && (
                      <StyledPaymentMethodAccountNumberWrapper>
                        <PaymentMethodAccountNumber short={true} fontSize="small" cardType={payout?.paymentDestination?.network} cardNumber={payout?.paymentDestination?.last4} dataCy="payment-method-account-number" />
                      </StyledPaymentMethodAccountNumberWrapper>
                    )}
                  </PaymentMethodWrapper>

                  <PayoutItemNet data-cy="payout-card-amount" isCanceled={payout.status === PayoutStatus.CANCELED}>
                    {centsToDollarsDisplay(payout.net)}
                  </PayoutItemNet>
                </PayoutStatusItem>
              </React.Fragment>
            );
          })
        )}
      </PayoutStatusWrapper>

      {!isLoading && payoutGroup?.notes && (
        <Row justify={Align.START} gap={4} data-cy="payout-card-notes">
          <Icon icon="Document" fill={theme.color.neutral[500]} width={11} height={13} />
          <Text size="xxsmall" style={{ color: theme.color.neutral[500] }}>
            {payoutGroup?.notes}
          </Text>
        </Row>
      )}
    </Layout>
  );
};

export default PayoutGroupCard;
